import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { googleLogout } from '@react-oauth/google';
import { TabBar } from './TabBar';
import { TifosiCrest } from '@/components/ui/TifosiCrest';
import { Icon } from '@/components/ui/Icon';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import { ThemeToggleButton } from '@/components/theme/ThemeToggleButton';
import { useProfileStore } from '@/stores/profileStore';
import { captureTrafficSource } from '@/utils/trafficSource';

const TAB_ROUTES = ['/', '/map', '/scout', '/favorites', '/clubs', '/profile'];

export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const colors = useTheme();
  const { picture, googleId, loadProfile, signOut } = useProfileStore();
  const showTabs = TAB_ROUTES.includes(location.pathname);

  useEffect(() => { void loadProfile(); }, [loadProfile]);
  useEffect(() => { captureTrafficSource(); }, []);

  const handleSignOut = async () => {
    try { googleLogout(); } catch { /* ignore */ }
    await signOut();
  };

  const profileActive = location.pathname.startsWith('/profile');

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        maxWidth: 600,
        margin: '0 auto',
        backgroundColor: colors.background,
        position: 'relative',
        paddingTop: 'env(safe-area-inset-top)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '10px 12px 10px 16px',
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor: colors.background,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <TifosiCrest size={30} text="T" fontSize={18} />
          <span className="tifosi-wordmark" style={{ fontSize: 20, color: colors.text }}>
            TIFOSI
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ThemeToggleButton />
          <button
            onClick={() => navigate('/profile')}
            aria-label="Mon profil"
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${profileActive ? Colors.primary : colors.border}`,
              backgroundColor: colors.surface,
              overflow: 'hidden',
              cursor: 'pointer',
            }}
          >
            {googleId && picture ? (
              <img
                src={picture}
                alt="Profil"
                referrerPolicy="no-referrer"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Icon
                name={profileActive ? 'person' : 'person-outline'}
                size={20}
                color={profileActive ? Colors.primary : colors.text}
              />
            )}
          </button>
          {googleId && (
            <button
              onClick={handleSignOut}
              aria-label="Se déconnecter"
              title="Se déconnecter"
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.surface,
                cursor: 'pointer',
              }}
            >
              <Icon name="log-out-outline" size={20} color={Colors.primary} />
            </button>
          )}
        </div>
      </header>

      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Outlet />
      </div>
      {showTabs && <TabBar />}
    </div>
  );
}
